import { isCrawler } from '@/seo/site';

/**
 * Visit counter for the admin's "Visitas" view (netlify/functions/pulse, lib/visits.mjs). No cookies and nothing
 * personal: the visit keeps, in this browser, where it came from (Google, an ad, Instagram…) for 30 minutes so
 * what the visitor does next counts for that source. Lines are grouped and sent when the page is left, to keep
 * the server calls (and their cost) to about two per visit.
 */

export type VisitEvent = 'tour' | 'quote' | 'whatsapp' | 'booking' | 'pix' | 'card' | 'mp';

const ENDPOINT = '/.netlify/functions/pulse';
const SESSION_KEY = 'aquamarina_visit';
/** "1" on a device that should not be counted (the owner's): set by ?visitas=off, cleared by ?visitas=on. */
export const VISITS_OFF_KEY = 'aquamarina_visits_off';
/** Devices where the admin is signed in are the owner's: never counted. */
const ADMIN_SESSION_KEY = 'aquamarina_admin_session';
const SESSION_MS = 30 * 60_000;
const FLUSH_MS = 60_000;

interface Session {
  id: string;
  src: string;
  camp: string;
  last: number;
  tour?: boolean;
}

type Line = ['pv', string] | ['ev', VisitEvent];

let queue: Line[] = [];
let pending: { n: boolean; land: string; lang: string } | null = null;
let timer: number | undefined;
let started = false;

const storage = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export function visitsDisabled(): boolean {
  if (typeof window === 'undefined' || isCrawler(navigator.userAgent) || navigator.webdriver) return true;
  const store = storage();
  return !store || store.getItem(VISITS_OFF_KEY) === '1' || Boolean(store.getItem(ADMIN_SESSION_KEY));
}

const PAID = new Set(['cpc', 'ppc', 'paid', 'paidsocial', 'paid_social', 'paid-social', 'ads', 'ad', 'cpm', 'display', 'sponsored']);
const word = (value: string | null | undefined, max = 40) => (value || '').toLowerCase().trim().replace(/[^a-z0-9.-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, max);

/** Where a site the visitor came from belongs: "instagram|social", "google|organic"… */
function fromReferrer(host: string): string {
  const is = (pattern: RegExp) => pattern.test(host);
  if (is(/(^|\.)gemini\.google\./)) return 'gemini|ai';
  if (is(/(^|\.)mail\.google\.|outlook\.|mail\.yahoo\./)) return 'email|email';
  if (is(/(^|\.)google\./)) return 'google|organic';
  if (is(/(^|\.)bing\.com$/)) return 'bing|organic';
  if (is(/(^|\.)duckduckgo\.com$/)) return 'duckduckgo|organic';
  if (is(/(^|\.)yahoo\./)) return 'yahoo|organic';
  if (is(/(^|\.)ecosia\.org$/)) return 'ecosia|organic';
  if (is(/(^|\.)instagram\.com$/)) return 'instagram|social';
  if (is(/(^|\.)(facebook\.com|fb\.com|fb\.me|messenger\.com)$/)) return 'facebook|social';
  if (is(/(^|\.)(t\.co|twitter\.com|x\.com)$/)) return 'x|social';
  if (is(/(^|\.)(youtube\.com|youtu\.be)$/)) return 'youtube|social';
  if (is(/(^|\.)tiktok\.com$/)) return 'tiktok|social';
  if (is(/(^|\.)(whatsapp\.com|wa\.me)$/)) return 'whatsapp|social';
  if (is(/(^|\.)(chatgpt\.com|openai\.com)$/)) return 'chatgpt|ai';
  if (is(/(^|\.)perplexity\.ai$/)) return 'perplexity|ai';
  if (is(/(^|\.)copilot\.microsoft\.com$/)) return 'copilot|ai';
  if (is(/(^|\.)claude\.ai$/)) return 'claude|ai';
  if (is(/(^|\.)tripadvisor\./)) return 'tripadvisor|referral';
  return `${word(host.replace(/^www\./, ''), 48) || 'outro'}|referral`;
}

/** Where this visit came from, read once when it starts (ad tags, then the previous site, then the app it opened in). */
function sourceOfVisit(): { src: string; camp: string } {
  const query = new URLSearchParams(window.location.search);
  const camp = (query.get('utm_campaign') || '').slice(0, 60);
  if (query.get('gclid') || query.get('gbraid') || query.get('wbraid')) return { src: 'google|cpc', camp };
  if (query.get('msclkid')) return { src: 'bing|cpc', camp };
  if (query.get('ttclid')) return { src: 'tiktok|paid', camp };
  const utmSource = word(query.get('utm_source'));
  if (utmSource) {
    const medium = word(query.get('utm_medium'), 20);
    const normalized = PAID.has(medium) ? (utmSource === 'google' || utmSource === 'bing' ? 'cpc' : 'paid') : medium.replace(/[^a-z]/g, '') || 'referral';
    return { src: `${utmSource}|${normalized.slice(0, 12)}`, camp };
  }
  let host = '';
  try {
    host = document.referrer ? new URL(document.referrer).hostname.toLowerCase() : '';
  } catch {
    host = '';
  }
  if (host && host !== window.location.hostname) return { src: fromReferrer(host), camp };
  // In-app browsers often send no referrer: Instagram's bio link and stories, Facebook, TikTok.
  const ua = navigator.userAgent;
  if (/Instagram/i.test(ua)) return { src: 'instagram|social', camp };
  if (/FBAN|FBAV|FB_IAB|FBIOS/i.test(ua)) return { src: 'facebook|social', camp };
  if (/TikTok|musical_ly|BytedanceWebview/i.test(ua)) return { src: 'tiktok|social', camp };
  if (query.get('fbclid')) return { src: 'facebook|social', camp };
  return { src: 'direct|none', camp };
}

const randomId = () => Math.random().toString(36).slice(2, 12);

function readSession(): Session | null {
  try {
    const raw = storage()?.getItem(SESSION_KEY);
    const session = raw ? (JSON.parse(raw) as Session) : null;
    return session && Date.now() - session.last < SESSION_MS ? session : null;
  } catch {
    return null;
  }
}

function saveSession(session: Session) {
  try {
    storage()?.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    /* private mode: the visit still counts, its source just is not remembered */
  }
}

/** The current visit, started (and its source read) when there is none or the last one went quiet. */
function currentSession(): Session {
  const existing = readSession();
  if (existing) {
    existing.last = Date.now();
    saveSession(existing);
    return existing;
  }
  const session: Session = { id: randomId(), ...sourceOfVisit(), last: Date.now() };
  saveSession(session);
  pending = { n: true, land: window.location.pathname.toLowerCase(), lang: (document.documentElement.lang || 'pt').slice(0, 2) };
  return session;
}

function flush() {
  window.clearTimeout(timer);
  timer = undefined;
  if (!queue.length && !pending) return;
  const session = readSession();
  if (!session) {
    queue = [];
    pending = null;
    return;
  }
  const body = JSON.stringify({ v: 1, s: session.id, src: session.src, camp: session.camp, ...(pending || { n: false }), e: queue.slice(0, 40) });
  queue = [];
  pending = null;
  try {
    const sent = typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
    if (!sent) void fetch(ENDPOINT, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'application/json' } }).catch(() => undefined);
  } catch {
    /* never in the visitor's way */
  }
}

function add(line: Line, now = false) {
  if (visitsDisabled()) return;
  start();
  const isNew = !readSession();
  currentSession();
  queue.push(line);
  // A new visit is sent right away, so it counts even if the page is closed before the next flush.
  if (now || isNew || queue.length >= 25) flush();
  else if (timer === undefined) timer = window.setTimeout(flush, FLUSH_MS);
}

export function recordPageView(pathname: string) {
  const path = pathname.toLowerCase();
  add(['pv', path]);
  // "Saw a tour": once per visit, the funnel's second step.
  if (/^(\/(es|en|fr))?\/passeio\//.test(path)) {
    const session = readSession();
    if (session && !session.tour) {
      saveSession({ ...session, tour: true });
      add(['ev', 'tour']);
    }
  }
}

export function recordVisitEvent(name: VisitEvent) {
  add(['ev', name], name !== 'tour');
}

/** Requests that mean the visitor is booking: counted when the server answers OK (the checkout is not touched). */
const BOOKING_CALLS: Array<[RegExp, VisitEvent]> = [
  [/\/create-pix-payment(?:[?#]|$)/, 'pix'],
  [/\/process-card-payment(?:[?#]|$)/, 'card'],
  [/\/create-payment(?:[?#]|$)/, 'mp'],
  [/\/reservation-save(?:[?#]|$)/, 'booking'],
];

function watchBookingCalls() {
  const original = window.fetch;
  if (typeof original !== 'function' || (original as { visitsWatch?: boolean }).visitsWatch) return;
  const watched = function (this: unknown, input: Parameters<typeof window.fetch>[0], init?: Parameters<typeof window.fetch>[1]) {
    const call = original.call(window, input, init);
    try {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      const method = (init?.method || (typeof input === 'object' && 'method' in input ? input.method : 'GET')).toUpperCase();
      const match = method === 'POST' ? BOOKING_CALLS.find(([pattern]) => pattern.test(url)) : undefined;
      if (match) call.then((response) => { if (response.ok) recordVisitEvent(match[1]); }, () => undefined);
    } catch {
      /* never in the visitor's way */
    }
    return call;
  } as typeof window.fetch & { visitsWatch?: boolean };
  watched.visitsWatch = true;
  window.fetch = watched;
}

/** Installs the listeners once: leaving the page sends what is waiting; WhatsApp links and booking calls count. */
function start() {
  if (started) return;
  started = true;
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(); });
  window.addEventListener('pagehide', flush);
  document.addEventListener('click', (event) => {
    const link = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (link && /^(https?:\/\/(wa\.me|api\.whatsapp\.com|web\.whatsapp\.com)|whatsapp:)/i.test(link.href)) recordVisitEvent('whatsapp');
  }, { capture: true });
  watchBookingCalls();
}

/** ?visitas=off on any page stops counting this device (the owner's phone or computer); ?visitas=on undoes it. */
export function applyVisitsSwitch() {
  try {
    const value = new URLSearchParams(window.location.search).get('visitas');
    if (value === 'off') storage()?.setItem(VISITS_OFF_KEY, '1');
    if (value === 'on') storage()?.removeItem(VISITS_OFF_KEY);
  } catch {
    /* storage blocked */
  }
}
